import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Clock, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { fadeInUp, staggerContainer, FORMSPREE_URL, WHATSAPP_URL } from "@/lib/constants";

export const ContactoSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          nombre: formData.name,
          empresa: formData.company,
          email: formData.email,
          telefono: formData.phone,
          mensaje: formData.message
        })
      });

      if (response.ok) {
        toast.success("¡Mensaje enviado! Te contacto en menos de 24 horas.");
        setFormData({ name: "", company: "", email: "", phone: "", message: "" });
      } else {
        throw new Error("Error en el envío");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar. Inténtalo de nuevo o escríbeme por WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="relative section-padding bg-slate-900/50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-12"
        >
          <motion.div variants={fadeInUp}>
            <span className="text-indigo-400 font-medium text-sm uppercase tracking-wider">Contacto</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 mt-4 line-decoration">
              ¿Hablamos de tu negocio?
            </h2>
            <p className="text-slate-400 mb-8 text-base leading-relaxed">
              Cuéntame qué pasa en tu empresa. Si puedo ayudarte, te lo digo claro.
              Si no, también. Sin rodeos.
            </p>

            <div className="glass-card rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">
                En el diagnóstico gratuito revisamos:
              </h3>
              <ul className="space-y-3">
                {[
                  "Dónde estás perdiendo dinero sin saberlo",
                  "Qué datos tienes y cómo aprovecharlos mejor",
                  "2-3 acciones concretas para mejorar resultados ya"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-3 text-slate-400 mb-2">
              <Clock className="w-5 h-5 text-indigo-400" />
              <span>Respuesta en menos de 24 horas</span>
            </div>
            <div className="flex items-center gap-3 text-slate-400 mb-2">
              <Mail className="w-5 h-5 text-indigo-400" />
              <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
            </div>
            <div className="flex items-center gap-3 text-slate-400 mb-4">
              <Phone className="w-5 h-5 text-indigo-400" />
              <a href="tel:+34672972514" className="hover:text-white transition-colors">672 972 514</a>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30 font-medium"
              data-testid="contact-whatsapp-link"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Escríbeme por WhatsApp
            </a>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                      Nombre *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-slate-700 text-white input-focus"
                      placeholder="Tu nombre"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-300 mb-2">
                      Empresa *
                    </label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-slate-700 text-white input-focus"
                      placeholder="Nombre de tu empresa"
                      data-testid="input-company"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-slate-800 border-slate-700 text-white input-focus"
                      placeholder="tu@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
                      Teléfono (opcional)
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-700 text-white input-focus"
                      placeholder="+34 600 000 000"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                      ¿Qué pasa en tu negocio? *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="bg-slate-800 border-slate-700 text-white input-focus resize-none"
                      placeholder="Cuéntame brevemente qué problemas tienes y qué te gustaría mejorar..."
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-glow bg-indigo-600 hover:bg-indigo-700 text-white py-6 rounded-full"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? "Enviando..." : "Revisa mi negocio sin compromiso"}
                    {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
